import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Search, Activity, Building2, Mail, ShieldCheck, Database,
  Target, Globe2, Sparkles, Megaphone, Send, ListOrdered, Truck, MessageSquareReply, Ban,
  Users, FileCheck, UserCheck, Crown, Bot, Server, Webhook, Lock, ScrollText, Settings,
  LogOut, Bell, Menu, X, Compass, Rocket, UserPlus, Handshake,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { group: "Command Center", items: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/search", label: "Global Search", icon: Search },
    { to: "/admin/activity", label: "Live Activity", icon: Activity },
  ]},
  { group: "Discovery", items: [
    { to: "/admin/companies", label: "Companies", icon: Building2 },
    { to: "/admin/contacts", label: "Contacts", icon: Mail },
    { to: "/admin/verification", label: "Verification", icon: ShieldCheck },
    { to: "/admin/sources", label: "Sources", icon: Database },
  ]},
  { group: "Intelligence", items: [
    { to: "/admin/scores", label: "Opportunity Scores", icon: Target },
    { to: "/admin/market", label: "Market Intelligence", icon: Globe2 },
    { to: "/admin/insights", label: "AI Insights", icon: Sparkles },
  ]},
  { group: "Outreach", items: [
    { to: "/admin/campaigns", label: "Campaigns", icon: Megaphone },
    { to: "/admin/invitations", label: "Invitations", icon: Send },
    { to: "/admin/queue", label: "Email Queue", icon: ListOrdered },
    { to: "/admin/delivery", label: "Delivery", icon: Truck },
    { to: "/admin/responses", label: "Responses", icon: MessageSquareReply },
    { to: "/admin/suppression", label: "Suppression", icon: Ban },
  ]},
  { group: "Membership", items: [
    { to: "/admin/register-company", label: "Register Company", icon: Rocket },
    { to: "/admin/create-membership", label: "Create Membership", icon: UserPlus },
    { to: "/admin/prospects", label: "Prospects", icon: Users },
    { to: "/admin/applications", label: "Applications", icon: FileCheck },
    { to: "/admin/approval", label: "Approval", icon: UserCheck },
    { to: "/admin/members", label: "Active Members", icon: Crown },
    { to: "/admin/plans", label: "Membership Plans", icon: FileCheck },
  ]},
  { group: "System", items: [
    { to: "/admin/automation", label: "Automation", icon: Bot },
    { to: "/admin/email-infra", label: "Email Infrastructure", icon: Server },
    { to: "/admin/webhooks", label: "API / Webhooks", icon: Webhook },
    { to: "/admin/security", label: "Security", icon: Lock },
    { to: "/admin/audit", label: "Audit Logs", icon: ScrollText },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ]},
  { group: "Full Consoles", items: [
    { to: "/admin/outreach", label: "Outreach Console", icon: Compass },
    { to: "/admin/events", label: "Events & Organisers", icon: Megaphone },
    { to: "/admin/partners", label: "Partner Station", icon: Users },
    { to: "/admin/referrals", label: "Referral Approvals", icon: Handshake },
  ]},
];

const MOBILE_NAV = [
  { to: "/admin/search", label: "Search", icon: Search },
  { to: "/admin/companies", label: "Results", icon: Building2 },
  { to: "/admin/verification", label: "Verify", icon: ShieldCheck },
  { to: "/admin/queue", label: "Queue", icon: ListOrdered },
  { to: "/admin/activity", label: "Activity", icon: Activity },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [drawer, setDrawer] = useState(false);

  useEffect(() => { if (user === false) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => {
    api.get("/admin/automation/status").then((r) => setStatus(r.data)).catch(() => navigate("/admin/login"));
    api.get("/admin/alerts").then((r) => setAlerts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [navigate]);

  const ALERT_ROUTES = {
    email_provider: "/admin/email-infra", email_failed: "/admin/delivery", bounce: "/admin/delivery",
    membership: "/admin/applications", data: "/admin/contacts", suppression: "/admin/suppression",
  };
  const ALERT_DOT = { critical: "bg-red-500", warning: "bg-amber-500", info: "bg-blue-500" };

  const ready = status && (status.state || "").includes("READY");

  const sidebar = (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-6 pb-5 border-b border-[#E4EAF3]">
        <Link to="/" className="block" data-testid="admin-logo" title="Back to 10XGLOBAL home">
          <span className="logo-trail-wrap rounded-sm bg-white px-1.5 py-0.5 inline-block">
            <img src="https://customer-assets-eiarnc6j.emergentagent.net/job_b2b-intelligence-2/artifacts/08rr2gpw_file_0000000021d08208bdb939d8b8dd4a57.png" alt="10XGLOBAL" className="h-11 w-auto block" />
            <span className="logo-trail-glow" aria-hidden="true" />
            <span className="logo-trail-spark" aria-hidden="true" />
          </span>
          <p className="text-[9px] font-mono uppercase tracking-[.25em] text-[#7C8AA3] mt-1.5">Command Center</p>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="admin-sidebar">
        {NAV.map((g) => (
          <div key={g.group}>
            <p className="px-3 mb-1.5 text-[9px] font-mono uppercase tracking-[.2em] text-[#93A3BC]">{g.group}</p>
            <div className="space-y-0.5">
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setDrawer(false)}
                  data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => `adm-side-link ${isActive ? "active" : ""}`}>
                  <it.icon size={15} /> {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
    </div>
  );

  return (
    <div className="adm-shell" data-testid="admin-layout">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-[#E4EAF3] z-30">{sidebar}</aside>
      {/* Mobile drawer */}
      {drawer && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-[#0B1B33]/40" onClick={() => setDrawer(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-2xl">{sidebar}</aside>
        </div>
      )}

      <div className="lg:pl-64 flex flex-col min-h-screen">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-[#E4EAF3] px-4 md:px-8 py-3 flex items-center gap-3" data-testid="admin-header">
          <button className="lg:hidden p-2 text-[#43546D]" onClick={() => setDrawer(true)} data-testid="mobile-menu-btn" aria-label="Open menu"><Menu size={19} /></button>
          <p className="text-[11px] font-mono uppercase tracking-[.2em] text-[#7C8AA3]">10XGLOBAL Admin</p>
          <div className="flex-1" />
          <span className="adm-pill text-emerald-700 border-emerald-300 bg-emerald-50" data-testid="system-status">
            <span className={`w-1.5 h-1.5 rounded-full ${ready ? "bg-emerald-500" : "bg-amber-500"}`} />
            {status ? (ready ? "Operational" : "Attention") : "…"}
          </span>
          <div className="relative">
            <button onClick={() => setShowAlerts((s) => !s)} className="relative p-2 text-[#43546D] hover:text-[#0B1B33] transition-colors"
              data-testid="notifications-bell" aria-label="Notifications">
              <Bell size={17} />
              {alerts.length > 0 && <span className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#2563EB] text-white text-[9px] font-bold flex items-center justify-center">{alerts.length}</span>}
            </button>
            {showAlerts && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowAlerts(false)} data-testid="notifications-backdrop" />
                <div className="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-white border border-[#E4EAF3] rounded-md shadow-xl z-50 overflow-hidden" data-testid="notifications-dropdown">
                  <p className="px-4 py-2.5 text-[9px] font-mono uppercase tracking-[.2em] text-[#93A3BC] border-b border-[#E4EAF3]">Notifications ({alerts.length})</p>
                  {alerts.length === 0 ? (
                    <p className="px-4 py-6 text-[12px] text-[#5B6B87] text-center" data-testid="notifications-empty">All clear — no alerts right now.</p>
                  ) : alerts.map((a, i) => (
                    <button key={i} onClick={() => { setShowAlerts(false); navigate(ALERT_ROUTES[a.kind] || "/admin"); }}
                      className="w-full text-left px-4 py-3 flex items-start gap-2.5 hover:bg-[#F8FAFE] transition-colors border-b border-[#F0F4FA] last:border-0"
                      data-testid={`alert-item-${i}`}>
                      <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${ALERT_DOT[a.level] || "bg-slate-400"}`} />
                      <span className="text-[12px] text-[#22355A] leading-snug">{a.text}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <span className="hidden sm:block text-[12px] font-medium text-[#22355A] max-w-[180px] truncate" data-testid="admin-profile">{user?.email || user?.phone || "Admin"}</span>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} data-testid="admin-logout-btn"
            className="p-2 text-[#43546D] hover:text-red-600 transition-colors" aria-label="Log out"><LogOut size={16} /></button>
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 md:py-8 pb-24 lg:pb-10 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E4EAF3] grid grid-cols-5" data-testid="admin-mobile-nav">
        {MOBILE_NAV.map((it) => (
          <NavLink key={it.to} to={it.to} data-testid={`mobile-nav-${it.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[9px] font-mono uppercase tracking-wider ${isActive ? "text-[#2563EB]" : "text-[#93A3BC]"}`}>
            <it.icon size={16} />{it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
